import { z } from 'zod';
import {
  familyStatuses,
  patientSexes,
  referralSources,
  ssnCountries,
  type AlcoholConsumption,
  type FamilyStatus,
  type HandPreference,
  type Language,
  type PatientSex,
  type QuantityPeriod,
  type ReferralSource,
  type SmokerStatus,
  type SsnCountry,
} from '../enums.js';
import { SSN_MAX_LENGTH } from '../validation/ssn.js';
import { residenceCountrySchema } from '../validation/country.js';
import { dateOnlySchema, lifestyleSchema, type PatientSummaryDto } from './patients.js';

/**
 * Contrat réseau de l'auto-inscription patient publique — l'écran `/p/:token` de `docs/SCREENS.md`
 * et son réglage « Inscription publique » côté cabinet.
 *
 * Le schéma de soumission est **strict et réduit**, écrit à la main. La V1 acceptait sur cette
 * route anonyme le schéma patient entier dérivé de la table (`docs/audit/audit-security.md` §3.3) :
 * un inconnu pouvait y poser `sharingConsent`, `archived`, `isTemporary` ou n'importe quel champ
 * clinique. Ici, tout ce qui n'est pas listé ci-dessous fait un 422, et les drapeaux internes
 * (consentement au partage, archivage, suppression, caractère temporaire) sont décidés par le
 * serveur seul.
 *
 * « Strict et réduit » ne veut pas dire « amputé » : la liste couvre exactement les sections
 * décrites par `docs/SCREENS.md` § « Pré-inscription patient publique » — identité, contact,
 * profil, matricule — et rien de plus. Les énumérations viennent d'`enums.js` et le pays de
 * `validation/country.js` : un formulaire public ne redéfinit pas le vocabulaire du dossier, sinon
 * il finit par en diverger.
 */

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * Saisie vide d'un champ facultatif. Le formulaire tient tout en texte : un sélecteur ou un
 * nombre jamais touché part souvent `""`, et le contrat dit « absent », pas « chaîne vide ».
 */
const blankToNull = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() === '') return null;
  return value;
};

function optionalBlank<Schema extends z.ZodType>(schema: Schema) {
  return z.preprocess(blankToNull, schema);
}

/** Même chose, mais le champ disparaît : un pays ou un jeton vide ne veut rien dire. */
const blankToAbsent = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() === '') return undefined;
  return value;
};

/**
 * Code postal saisi en salle d'attente. Le champ est facultatif : vide, il est absent, quel que
 * soit le pays de résidence.
 *
 * Les formes courantes passent, y compris quand le pays reste le Luxembourg (valeur proposée par
 * le formulaire) : `L-2449`, `LU 2449`, tiret ASCII ou typographique, espaces, et un code étranger
 * court (`75001`, `12345`, Belgique, Allemagne). On retire le préfixe pays connu et les espaces ou
 * tirets qui ne font que séparer les chiffres. Les chiffres stockés sont ceux qui ont été saisis.
 * On refuse une lettre qui n'est pas ce préfixe, moins de 3 chiffres, plus de 10 chiffres, ou plus
 * de 20 caractères en tout.
 */
const POSTAL_CODE_MAX_LENGTH = 20;

/** Tirets qu'un copier-coller apporte à la place du trait d'union ASCII, plus le signe moins. */
const TYPOGRAPHIC_DASH = /[\u2010\u2011\u2012\u2013\u2014\u2212]/g;

/**
 * Préfixes qu'on sait retirer. Les plus longs d'abord : `LU` avant `L`, sinon le `L` avale le
 * préfixe et laisse une lettre au milieu.
 */
const POSTAL_PREFIX_SOURCE = 'LU|FR|BE|DE|CH|L|F|B|D';

const POSTAL_CODE_SHAPE = new RegExp(
  `^[\\s-]*(?:(?:${POSTAL_PREFIX_SOURCE})[\\s-]*)?(?:\\d[\\s-]*){3,10}$`,
  'i',
);

const POSTAL_PREFIX = new RegExp(`^(?:${POSTAL_PREFIX_SOURCE})(?=[\\s-]*\\d)`, 'i');

function tidyPostalCode(value: string): string {
  return value.replace(TYPOGRAPHIC_DASH, '-').replace(/\s+/g, ' ').trim();
}

/** Chiffres saisis, sans préfixe pays ni séparateur. N'ajoute ni ne tronque aucun chiffre. */
function digitsOfPostalCode(value: string): string {
  return value.replace(POSTAL_PREFIX, '').replace(/[\s-]+/g, '');
}

const postalCodeSchema = z
  .string()
  .trim()
  .max(POSTAL_CODE_MAX_LENGTH)
  .transform(tidyPostalCode)
  .pipe(
    z.union([
      z.literal('').transform(() => null),
      z.string().regex(POSTAL_CODE_SHAPE, 'Code postal invalide').transform(digitsOfPostalCode),
    ]),
  )
  .nullable()
  .optional();

/** Même forme que le mode de vie du dossier : on la reprend plutôt que de la réécrire. */
const physicalActivitiesSchema = lifestyleSchema.shape.physicalActivities;

export const publicRegistrationTokenParamSchema = z
  .object({ token: z.string().trim().min(16).max(200) })
  .strict();

export const publicRegistrationSubmitSchema = z
  .object({
    firstName: trimmed(100),
    lastName: trimmed(100),
    birthDate: dateOnlySchema,
    sex: optionalBlank(z.enum(patientSexes).nullable().optional()),
    /** Exigé : c'est par là que le cabinet revient vers le patient, et l'accusé du formulaire. */
    email: z.email().max(254),
    phone: optionalText(40),
    mobilePhone: optionalText(40),
    street: optionalText(160),
    houseNumber: optionalText(20),
    addressDetails: optionalText(160),
    postalCode: postalCodeSchema,
    city: optionalText(120),
    /**
     * Pays de **résidence**, code ISO 3166-1 alpha-2. Sans effet métier : le cabinet reste
     * luxembourgeois, mais sa patientèle est frontalière. Absent, la colonne vaut `LU`.
     */
    country: z.preprocess(blankToAbsent, residenceCountrySchema.optional()),

    /*
     * Profil. Tout y est facultatif : ce formulaire fait gagner du temps au praticien, il ne
     * conditionne rien, et un patient pressé doit pouvoir s'arrêter après son adresse.
     */
    profession: optionalText(120),
    familyStatus: optionalBlank(z.enum(familyStatuses).nullable().optional()),
    numberOfChildren: optionalBlank(z.int().min(0).max(30).nullable().optional()),
    physicalActivities: physicalActivitiesSchema,
    /** « Comment avez-vous connu le cabinet ? » */
    referralSource: optionalBlank(z.enum(referralSources).nullable().optional()),

    /**
     * Facultatifs, et revalidés côté serveur par `ssnIssue` — la V1 ne vérifiait qu'au
     * navigateur. Le pays par défaut est le Luxembourg quand le numéro vient sans lui.
     */
    ssnCountry: z.preprocess(blankToAbsent, z.enum(ssnCountries).optional()),
    ssn: optionalText(SSN_MAX_LENGTH),
    /** Motif de consultation, texte libre court. Rangé dans les notes administratives du dossier. */
    reason: optionalText(500),
    /** Médecin traitant : colonne du dossier, pas du mode de vie. */
    primaryPhysician: optionalText(160),
    /**
     * Note du patient. Facultative. Elle reste dans `patientNote` : le motif, lui, va aux notes
     * administratives, et les deux ne se mélangent pas.
     */
    patientNote: optionalText(5000),
    smokerStatus: optionalBlank(lifestyleSchema.shape.smokerStatus),
    smokerQuantity: optionalBlank(lifestyleSchema.shape.smokerQuantity),
    smokerQuantityPeriod: optionalBlank(lifestyleSchema.shape.smokerQuantityPeriod),
    alcoholConsumption: optionalBlank(lifestyleSchema.shape.alcoholConsumption),
    alcoholQuantity: optionalBlank(lifestyleSchema.shape.alcoholQuantity),
    alcoholQuantityPeriod: optionalBlank(lifestyleSchema.shape.alcoholQuantityPeriod),
    handPreference: optionalBlank(lifestyleSchema.shape.handPreference),
    referenceHeight: optionalBlank(lifestyleSchema.shape.referenceHeight),
    referenceWeight: optionalBlank(lifestyleSchema.shape.referenceWeight),
    currentMedications: lifestyleSchema.shape.currentMedications,
    allergies: lifestyleSchema.shape.allergies,
    /** Consentement explicite au traitement des données, exigé vrai. Absent ou faux → 422. */
    dataProcessingConsent: z.literal(true),
    /**
     * Jeton de reprise rendu une fois au téléphone qui a commencé le dossier. Absent : nouveau
     * dossier. Il ne figure jamais dans l'URL du QR, qui reste le seul secret partagé en salle
     * d'attente.
     */
    resumeToken: z.preprocess(
      blankToAbsent,
      z
        .string()
        .regex(/^[A-Za-z0-9_-]{43,64}$/)
        .optional(),
    ),
  })
  .strict();
export type PublicRegistrationSubmitInput = z.infer<typeof publicRegistrationSubmitSchema>;

/** Validation d'un dossier temporaire : mêmes acquittements de doublons que la création ordinaire. */
export const temporaryPatientValidateSchema = z
  .object({
    acknowledgeDuplicates: z.boolean().optional(),
    ignoreDuplicateOf: z.array(z.uuid()).max(20).optional(),
  })
  .strict();
export type TemporaryPatientValidateInput = z.infer<typeof temporaryPatientValidateSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

export const publicRegistrationFieldNames = [
  'firstName',
  'lastName',
  'birthDate',
  'sex',
  'email',
  'phone',
  'mobilePhone',
  'street',
  'houseNumber',
  'addressDetails',
  'postalCode',
  'city',
  'country',
  'profession',
  'familyStatus',
  'numberOfChildren',
  'physicalActivities',
  'referralSource',
  'ssnCountry',
  'ssn',
  'reason',
  'dataProcessingConsent',
] as const;
export type PublicRegistrationFieldName = (typeof publicRegistrationFieldNames)[number];

export interface PublicRegistrationFieldDto {
  name: PublicRegistrationFieldName;
  required: boolean;
}

/**
 * Champs du formulaire public, dans l'ordre d'affichage — celui des sections de `docs/SCREENS.md` :
 * identité, contact, profil, matricule. Le serveur les annonce pour que la page publique n'ait rien
 * à deviner, et c'est tout ce qu'elle apprend du cabinet : ni membres, ni adresse, ni coordonnées.
 * Seuls l'identité, l'adresse email et le consentement sont exigés ; le reste fait gagner du temps
 * au praticien sans jamais retenir un patient pressé.
 */
export const publicRegistrationFields: readonly PublicRegistrationFieldDto[] = [
  { name: 'firstName', required: true },
  { name: 'lastName', required: true },
  { name: 'birthDate', required: true },
  { name: 'sex', required: false },
  { name: 'email', required: true },
  { name: 'phone', required: false },
  { name: 'mobilePhone', required: false },
  { name: 'street', required: false },
  { name: 'houseNumber', required: false },
  { name: 'addressDetails', required: false },
  { name: 'postalCode', required: false },
  { name: 'city', required: false },
  { name: 'country', required: false },
  { name: 'profession', required: false },
  { name: 'familyStatus', required: false },
  { name: 'numberOfChildren', required: false },
  { name: 'physicalActivities', required: false },
  { name: 'referralSource', required: false },
  { name: 'ssnCountry', required: false },
  { name: 'ssn', required: false },
  { name: 'reason', required: false },
  { name: 'dataProcessingConsent', required: true },
];

/** Réglage vu par le cabinet. `url` est nul tant que l'inscription publique est désactivée. */
export interface PublicRegistrationSettingsDto {
  enabled: boolean;
  url: string | null;
}

/** Tout ce que la page publique apprend du cabinet. */
export interface PublicRegistrationFormDto {
  organizationName: string;
  language: Language;
  fields: PublicRegistrationFieldDto[];
}

/**
 * Accusé de soumission. `reference` est une empreinte du dossier, pas son identifiant : le public
 * ne reçoit jamais de clé interne, et le cabinet peut recalculer la même référence si un patient
 * la lui cite.
 */
export interface PublicRegistrationResultDto {
  reference: string;
}

/**
 * Accusé d'un enregistrement reprenable. `resumeToken` ne sort qu'ici, vers le téléphone qui
 * vient de créer le dossier. Le QR du cabinet ne le contient pas.
 */
export interface PublicRegistrationDraftDto {
  reference: string;
  resumeToken: string;
}

/**
 * Dossier commencé en salle d'attente, tel que le praticien le reprend à la création d'une fiche.
 * Champs administratifs saisis par le patient, rien d'autre : pas la ligne, pas le jeton de reprise.
 */
export interface WaitingRoomIntakeDto {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  sex: PatientSex | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  street: string | null;
  houseNumber: string | null;
  addressDetails: string | null;
  postalCode: string | null;
  city: string | null;
  country: string;
  profession: string | null;
  familyStatus: FamilyStatus | null;
  numberOfChildren: number | null;
  referralSource: ReferralSource | null;
  ssn: string | null;
  ssnCountry: SsnCountry;
  reason: string | null;
  primaryPhysician: string | null;
  patientNote: string | null;
  smokerStatus: SmokerStatus | null;
  smokerQuantity: number | null;
  smokerQuantityPeriod: QuantityPeriod | null;
  alcoholConsumption: AlcoholConsumption | null;
  alcoholQuantity: number | null;
  alcoholQuantityPeriod: QuantityPeriod | null;
  handPreference: HandPreference | null;
  referenceHeight: number | null;
  referenceWeight: number | null;
  currentMedications: string | null;
  allergies: string | null;
  physicalActivities: { activity: string; frequency: string }[];
}

export interface TemporaryPatientsDto {
  items: PatientSummaryDto[];
}
